import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';

export const DEFAULT_TABLE_LIMIT = 1;

export const LIMIT_OPTIONS = [1, 10, 30, 50, 100] as const;

export type LimitOption = (typeof LIMIT_OPTIONS)[number];

type TableLimitsState = {
  globalLimit: LimitOption;
  tableLimits: Record<string, LimitOption>;
};

type TableLimitsActions = {
  setGlobalLimit: (limit: LimitOption) => void;
  setTableLimit: (tableKey: string, limit: LimitOption) => void;
  getTableLimit: (tableKey: string) => LimitOption;
  resetTableLimit: (tableKey: string) => void;
};

const tableLimitsSlice: StateCreator<TableLimitsState & TableLimitsActions> = (set, get) => ({
  globalLimit: DEFAULT_TABLE_LIMIT,
  tableLimits: {},

  setGlobalLimit: (limit: LimitOption) => set({ globalLimit: limit }),

  setTableLimit: (tableKey: string, limit: LimitOption) =>
    set(state => ({
      tableLimits: {
        ...state.tableLimits,
        [tableKey]: limit,
      },
    })),

  getTableLimit: (tableKey: string) => {
    const state = get();
    return state.tableLimits[tableKey] ?? state.globalLimit;
  },

  resetTableLimit: (tableKey: string) =>
    set(state => {
      const { [tableKey]: _, ...rest } = state.tableLimits;
      return { tableLimits: rest };
    }),
});

const persistedTableLimitsStore = persist<TableLimitsState & TableLimitsActions>(tableLimitsSlice, {
  name: `${window.location.origin}/table-limits`,
});

export const useTableLimitsStore = create(persistedTableLimitsStore);
